// 🧿 طلسم محافظ فایل
import React from "react";
// UI Framework
// Common Components
import { CheckBox, FileDownloader, Button, Modal } from "src/Components";
import TableTemplate, {
  IFilterTableCols,
} from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
// Local Components
import OrderBook from "./OrderBook";
import SearchParamUI from "./SearchParamUI";
import AssumptionsModal from "./AssumptionsModal";
import CalculatorModal from "./Calculator";
// Utilities
import * as R from "ramda";
// Requests
import {
  getPaiPositionExcel,
  getPairPositionTable,
} from "src/Requests/iMEMarketWatch";
// Types
import type {
  IPairPositionSearchParams,
  IPairPositionTableReport,
  ISelectedPairTableInfo,
} from "src/Interfaces/iMEMarketWatch";

interface IVisibleHeaders {
  all: boolean;
  mediator: boolean;
}

enum ModalMode {
  Closed,
  Assumption,
  Calculator,
}

const CommodityPairPosition = () => {
  // refs
  const prevRowRef = React.useRef<number | null>(null);
  // local store
  const [modalMode, setModalMode] = React.useState(ModalMode.Closed);
  const [visibleHeaders, setVisibleHeaders] = React.useState(
    {} as IVisibleHeaders,
  );
  const [serverSearchParams, setServerSearchParams] =
    React.useState<IPairPositionSearchParams>({} as IPairPositionSearchParams);
  const [searchParam, setSearchParam] =
    React.useState<IPairPositionSearchParams>({} as IPairPositionSearchParams);
  const [selectedTableInfo, setSelectedTableInfo] =
    React.useState<ISelectedPairTableInfo>({} as ISelectedPairTableInfo);

  const changeHandler = (name: string, value: any) => {
    setVisibleHeaders({ ...visibleHeaders, [name]: value } as IVisibleHeaders);
  };

  const handleExcelDownload = () => getPaiPositionExcel(searchParam);

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleSearch = () => {
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      searchParam,
    );
    setServerSearchParams(cleanObject as IPairPositionSearchParams);
  };

  const getFilterCols = (): IFilterTableCols => {
    const result: IFilterTableCols = {
      show: true,
      tableName: "coveredCallPairPositionTable",
      allowedPositions: [],
    };
    if (visibleHeaders.all) result.allowedPositions.push("DefaultHidden");
    if (visibleHeaders.mediator) result.allowedPositions.push("Mediator");
    return result;
  };

  const UIModal = React.useMemo(() => {
    switch (modalMode) {
      case ModalMode.Assumption:
        return (
          <AssumptionsModal onClose={() => setModalMode(ModalMode.Closed)} />
        );
      case ModalMode.Calculator:
        return (
          <CalculatorModal onClose={() => setModalMode(ModalMode.Closed)} />
        );
    }
    return null;
  }, [modalMode]);

  return (
    <div className="flex flex-col justify-start items-start gap-2 relative pt-5 mx-auto p-8 w-full">
      {modalMode ? (
        <Modal title={modalMode === ModalMode.Assumption ? "مفروضات" : "ماشین حساب"} UI={UIModal} onClose={() => setModalMode(ModalMode.Closed)} />
      ) : (
        <></>
      )}
      <div className="flex justify-start items-center w-full">
        <div className="flex flex-row justify-start items-center gap-5">
          <div className="bg-sky-200 rounded py-1 px-2">
            <CheckBox
              checked={visibleHeaders.all}
              label="جزئیات"
              name="all"
              onChange={changeHandler}
            />
          </div>
          <div className="bg-red-200 rounded py-1 px-2">
            <CheckBox
              checked={visibleHeaders.mediator}
              label="محاسبات"
              name="mediator"
              onChange={changeHandler}
            />
          </div>
          <FileDownloader getFile={handleExcelDownload} />
          <Button
            onClick={() => setModalMode(ModalMode.Assumption)}
            text="مفروضات"
            variant="outlined"
          />
          <Button
            onClick={() => setModalMode(ModalMode.Calculator)}
            text="ماشین حساب"
            variant="outlined"
          />
        </div>
      </div>
      <OrderBook selectedTableInfo={selectedTableInfo} />
      <div className="flex flex-row justify-start items-start gap-1 w-full mt-7 h-fit">
        <SearchParamUI
          searchParam={searchParam}
          searchParamChangeHandler={searchParamChangeHandler}
          onSubmit={handleSearch}
        />
        <div className="flex min-w-0 flex-1 justify-center items-center gap-3 overflow-x-hidden">
          <TableTemplate<IPairPositionSearchParams, IPairPositionTableReport>
            TableUI={({ reports, onAction, sortingType, setSortingType }) => (
              <CommonTableUI
                reports={reports}
                onAction={onAction}
                filterCols={getFilterCols()}
                sortingType={sortingType}
                setSortingType={setSortingType}
                onTableRowClick={(row) => {
                  if (prevRowRef.current === row.id) return;
                  prevRowRef.current = row.id;
                  setSelectedTableInfo({
                    id: row.id,
                    firstContractCode: row.firstContractCode,
                    secondContractCode: row.secondContractCode,
                  });
                }}
                activeRowId={
                  selectedTableInfo ? selectedTableInfo?.id : undefined
                }
                tableBoxClassName="max-h-[500px]"
                ref={prevRowRef}
              />
            )}
            refreshTableTime={10000}
            getDataTable={getPairPositionTable}
            searchParameters={serverSearchParams}
            withSort
          />
        </div>
      </div>
    </div>
  );
};

export default CommodityPairPosition;
